import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import {
  createAIConversationMutation,
  updateAIConversationMutation,
  deleteAIConversationMutation,
  createAttachmentsMutation,
  deleteAttachmentMutation,
} from "modules/CommonMutations";
import {
  createAIMessageMutation,
  deleteAIMessageMutation,
  updateAIMessageMutation,
  deleteMemoriesMutation,
} from "./GPTPlaygroundMutation";
import {
  getAIConversationsQueryWithPublicUrlFromCache,
  writeAIConversationsQueryWithPublicUrlInCache,
} from "modules/CommonGraphqlHelpers";
import {
  getAiConversationsQuery,
  getAIConversationsQueryWithPublicUrl,
} from "modules/CommonQuery";
import { getUserId } from "modules/CommonSelectors";
import { getAIResponse, getGeneratedImageUrl } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfofromStorage } from "services/localStorage";

import { generateRandomId } from "Utils";
import { getAIMemoriesQuery } from "./GPTPlaygroundQuery";

export let cancelRequest;

export const createAIConversation = ({ input }) => {
  return async (_dispatch, getState) => {
    try {
      const userId = getUserId(getState());

      return await client.mutate({
        mutation: createAIConversationMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAiConversationsQuery,
            variables: {
              filters: {
                entityType: "USER",
                entityId: userId,
              },
            },
          },
        ],
      });
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const updateAIConversation = ({ input }) => {
  return async (_dispatch, getState) => {
    try {
      const userId = getUserId(getState());

      return await client.mutate({
        mutation: updateAIConversationMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAiConversationsQuery,
            variables: {
              filters: {
                entityType: "USER",
                entityId: userId,
              },
            },
          },
        ],
      });
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const deleteAIConversation = ({ input }) => {
  return async (_dispatch, getState) => {
    try {
      const userId = getUserId(getState());

      return await client.mutate({
        mutation: deleteAIConversationMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAiConversationsQuery,
            variables: {
              filters: {
                entityType: "USER",
                entityId: userId,
              },
            },
          },
        ],
      });
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const createNewAIMessage = ({
  aiConversationId,
  authorType,
  query,
  isUserInputMessage = true,
  metadata = {},
  usageMetricsId,
  parentMessageId,
  awaitRefetchQueries = true,
}) => {
  return async (dispatch, getState) => {
    try {
      return await client.mutate({
        mutation: createAIMessageMutation,
        variables: {
          input: {
            conversationId: aiConversationId,
            authorType,
            content: query,
            metadata: {
              isUserInputMessage: isUserInputMessage,
              ...metadata,
            },
            usageMetricsId,
            parentMessageId,
          },
        },
        awaitRefetchQueries: awaitRefetchQueries,
        refetchQueries: [
          {
            query: getAIConversationsQueryWithPublicUrl,
            variables: {
              id: aiConversationId,
            },
          },
        ],
      });
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const createNewAiMessageInCache = ({
  aiConversationId,
  authorType,
  query,
  attachments = [],
}) => {
  return async (dispatch, getState) => {
    const cacheData = getAIConversationsQueryWithPublicUrlFromCache({
      id: aiConversationId,
    });
    try {
      let updatedCacheData = _.cloneDeep(cacheData);

      const messages = _.get(cacheData, "messages.edges", []);

      const generatedId = generateRandomId();
      const userInfo = getUserInfofromStorage();

      const {
        id: userId,
        first_name,
        middle_name,
        last_name,
        email,
        user_type,
        role,
        isDevUser,
      } = userInfo;

      const newMessage = {
        id: generatedId,
        role: null,
        authorType,
        content: query,
        metadata: {
          isUserInputMessage: true,
        },
        createdBy: {
          id: userId,
          firstName: first_name,
          middleName: middle_name,
          lastName: last_name,
          profileImage: null,
          email,
          type: user_type,
          showPitchScreen: true,
          role,
          showClassSelectionScreen: true,
          showBifurcationScreen: true,
          isDevUser,
          isArchived: false,
          entityTags: [],
          __typename: "Staff",
        },
        votes: {
          myVote: null,
          __typename: "VoteConnection",
        },
        usageMetricsId: null,
        parentMessage: null,
        attachments: attachments,
        __typename: "AiMessage",
      };

      const updatedMessages = [
        {
          node: newMessage,
          __typename: "AiMessageEdge",
        },
        ...messages,
      ];

      updatedCacheData = update(updatedCacheData, {
        messages: {
          edges: {
            $set: updatedMessages,
          },
        },
      });

      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: updatedCacheData,
      });

      return {
        generatedId,
        originalCacheData: cacheData,
        updatedCacheData,
        newMessage,
        updatedMessages,
      };
    } catch (err) {
      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: cacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const createNewAIMessageMutationWithTemporaryId = ({
  aiConversationId,
  authorType,
  query,
  generatedId,
  originalCacheData,
  updatedCacheData,
  newMessage,
  updatedMessages,
}) => {
  return async (dispatch, getState) => {
    try {
      const mutationData = await client.mutate({
        mutation: createAIMessageMutation,
        variables: {
          input: {
            conversationId: aiConversationId,
            authorType,
            content: query,
            metadata: {
              isUserInputMessage: true,
            },
          },
        },
      });

      const { createdBy, id: realMessageId } = _.get(
        mutationData,
        "data.ai.createAiMessage.response",
        {}
      );

      const updatedMessage = {
        ...newMessage,
        createdBy,
        id: realMessageId,
      };

      const updatedMessagesWithRealId = _.map(updatedMessages, message => {
        const messageId = _.get(message, "node.id");
        if (_.isEqual(messageId, generatedId)) {
          return {
            node: updatedMessage,
            __typename: "AiMessageEdge",
          };
        }

        return message;
      });

      updatedCacheData = update(updatedCacheData, {
        messages: {
          edges: {
            $set: updatedMessagesWithRealId,
          },
        },
      });

      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: updatedCacheData,
      });

      return {
        realMessageId,
      };
    } catch (err) {
      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: originalCacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const updateAIMessage = ({ aiConversationId, variables }) => {
  return async (dispatch, getState) => {
    const cacheData = getAIConversationsQueryWithPublicUrlFromCache({
      id: aiConversationId,
    });

    try {
      let updatedCacheData = _.cloneDeep(cacheData);

      const messages = _.get(cacheData, "messages.edges", []);

      const updatedMessages = _.map(messages, message => {
        const messageNode = _.get(message, "node", {});
        const messageId = _.get(messageNode, "id");
        if (_.isEqual(messageId, variables?.id)) {
          return {
            ...message,
            node: {
              ...messageNode,
              ...variables,
            },
          };
        }

        return message;
      });

      updatedCacheData = update(updatedCacheData, {
        messages: {
          edges: {
            $set: updatedMessages,
          },
        },
      });

      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: updatedCacheData,
      });

      await client.mutate({
        mutation: updateAIMessageMutation,
        variables: {
          input: variables,
        },
      });
    } catch (err) {
      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: cacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const deleteAIMessage = ({ aiConversationId, ids }) => {
  return async (dispatch, getState) => {
    const cacheData = getAIConversationsQueryWithPublicUrlFromCache({
      id: aiConversationId,
    });

    try {
      let updatedCacheData = _.cloneDeep(cacheData);

      const messages = _.get(cacheData, "messages.edges", []);

      const updatedMessages = _.filter(messages, message => {
        const messageId = _.get(message, "node.id");
        return !_.includes(ids, messageId);
      });

      updatedCacheData = update(updatedCacheData, {
        messages: {
          edges: {
            $set: updatedMessages,
          },
          totalCount: {
            $set: _.size(updatedMessages),
          },
        },
      });

      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: updatedCacheData,
      });

      await client.mutate({
        mutation: deleteAIMessageMutation,
        variables: {
          input: {
            ids,
          },
        },
      });
    } catch (err) {
      writeAIConversationsQueryWithPublicUrlInCache({
        variables: {
          id: aiConversationId,
        },
        data: cacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const onSendQuery = params => {
  const {
    t,
    query,
    aiConversationId,
    setQuery,
    setCurrentAIState,
    input,
    setToolName,
  } = params;

  return async (dispatch, getState) => {
    try {
      // setQuery("");

      // // 2. create a message from user input
      // if (query) {
      //   await dispatch(
      //     createAIMessage({
      //       variables: {
      //         input: {
      //           conversationId: aiConversationId,
      //           authorType: "user",
      //           content: query,
      //           metadata: {
      //             isUserInputMessage: true,
      //           },
      //           // parentMessageId: currentParentMessageId,
      //         },
      //       },
      //     })
      //   );
      // }

      // 3. show writing... animation
      setCurrentAIState("WRITING");

      // 4. generate AI response
      const conversation = getAIConversationsQueryWithPublicUrlFromCache({
        id: aiConversationId,
      });

      const parentMessageId = _.get(conversation, "messages.edges[0].node.id");

      const id = generateRandomId();
      let responseContent = {};

      const response = await dispatch(
        getAIResponse({
          variables: {
            input,
          },
          getCancelToken: token => (cancelRequest = token),
          onDownloadProgress: params => {
            const {
              fieldType,
              content,
              usageMetricsId,
              userInputFlagged,
              model,
              temperature,
              tools,
            } = params || {};

            // when user cancels the request, responseContent will contain the response generated till that point
            responseContent = params;

            const latestTool = _.last(tools);
            if (_.has(latestTool, "name")) {
              setToolName(`Calling ${latestTool.name}`);
            }

            if (content) {
              const createAiMessageNode = {
                node: {
                  id,
                  role: null,
                  authorType: "assistant",
                  content: userInputFlagged
                    ? t("common:user_input_flagged_msg")
                    : content,
                  metadata: {
                    fieldType,
                    userInputFlagged,
                    model,
                    temperature,
                  },
                  createdBy: null,
                  votes: {
                    myVote: null,
                    __typename: "VoteConnection",
                  },
                  usageMetricsId: usageMetricsId || null,
                  attachments: [],
                  parentMessage: {
                    id: null,
                    __typename: "AiMessage",
                  },
                  __typename: "AiMessage",
                },
                __typename: "AiMessageEdge",
              };

              const dataToSend = update(conversation, {
                messages: {
                  edges: {
                    $unshift: [createAiMessageNode],
                  },
                  totalCount: {
                    $set: _.get(conversation, "messages.totalCount", 0) + 1,
                  },
                },
              });
              writeAIConversationsQueryWithPublicUrlInCache({
                variables: {
                  id: aiConversationId,
                },
                data: dataToSend,
              });

              setToolName("");
              setCurrentAIState("STREAMING");
            }
          },
        })
      );

      // 5. hide "Stop generation" button and reset local states
      setCurrentAIState("");

      // 6. create a message of the generated response from AI
      const {
        content: aiResponse,
        fieldType,
        usageMetricsId,
        model,
        temperature,
        userInputFlagged = false,
      } = response || responseContent || {};

      if (!_.isEmpty(aiResponse) || userInputFlagged) {
        const content = userInputFlagged
          ? t("common:user_input_flagged_msg")
          : aiResponse;

        const response = await dispatch(
          createNewAIMessage({
            aiConversationId,
            authorType: "assistant",
            query: content,
            isUserInputMessage: false,
            metadata: {
              fieldType,
              userInputFlagged,
              model,
              temperature,
            },
            usageMetricsId,
            parentMessageId,
            awaitRefetchQueries: false,
          })
        );

        const aiConversation = getAIConversationsQueryWithPublicUrlFromCache({
          id: aiConversationId,
        });
        const updatedData = update(aiConversation, {
          messages: {
            edges: {
              0: {
                node: {
                  $set: _.get(response, "data.ai.createAiMessage.response", {}),
                },
              },
            },
          },
        });
        writeAIConversationsQueryWithPublicUrlInCache({
          variables: {
            id: aiConversationId,
          },
          data: updatedData,
        });
      }
    } catch (e) {
      setCurrentAIState("");
    }
  };
};

export const createAIConversationAttachments = ({
  input,
  aiConversationId,
}) => {
  return async (_dispatch, getState) => {
    try {
      const userId = getUserId(getState());

      return await client.mutate({
        mutation: createAttachmentsMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAIConversationsQueryWithPublicUrl,
            skip: _.isEmpty(aiConversationId),
            variables: {
              id: aiConversationId,
            },
          },
        ],
      });
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const deleteAIConversationAttachment = ({
  input,
  aiConversationId,
  deleteFromKnowledgeBase,
}) => {
  return async (_dispatch, getState) => {
    try {
      const userId = getUserId(getState());
      const attachmentIds = _.compact([_.get(input, "attachmentId")]);

      if (_.isEmpty(attachmentIds)) {
        return;
      }

      return await client.mutate({
        mutation: deleteAttachmentMutation,
        variables: {
          attachmentIds: attachmentIds,
          deleteFromKnowledgeBase,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAIConversationsQueryWithPublicUrl,
            skip: _.isEmpty(aiConversationId),
            variables: {
              id: aiConversationId,
            },
          },
        ],
      });
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const getAIMemoriesFromCache = ({ entityType }) => {
  try {
    const memories = client.readQuery({
      query: getAIMemoriesQuery,
      variables: {
        entityType,
      },
    });
    return memories;
  } catch (e) {
    return [];
  }
};

export const writeAIMemoriesInCache = ({ entityType, memories }) => {
  try {
    client.writeQuery({
      query: getAIMemoriesQuery,
      variables: {
        entityType,
      },
      data: memories,
    });
  } catch (e) {}
};

export const deleteAIMemories = ({ input }) => {
  return async () => {
    const existingMemories = getAIMemoriesFromCache({
      entityType: input.entityType,
    });
    try {
      const indiciesToRemove = _.get(input, "bioKeys", []);

      const filteredMemoriesArray = _.filter(
        _.get(existingMemories, "ai.aiMemory.edges", []),
        (memory, index) => !_.includes(indiciesToRemove, index + 1)
      );
      const updatedMemories = _.cloneDeep(existingMemories);
      _.set(updatedMemories, "ai.aiMemory.edges", filteredMemoriesArray);

      writeAIMemoriesInCache({
        entityType: input.entityType,
        memories: updatedMemories,
      });

      return await client.mutate({
        mutation: deleteMemoriesMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAIMemoriesQuery,
            variables: {
              entityType: input.entityType,
            },
          },
        ],
      });
    } catch (err) {
      writeAIMemoriesInCache({
        entityType: input.entityType,
        memories: existingMemories,
      });
      // no need to show error toast
      return {};
    }
  };
};

export const onGenerateImageQuery = params => {
  const {
    t,
    query,
    aiConversationId,
    setQuery,
    setCurrentAIState,
    input,
    setToolName,
  } = params;

  const {
    entityType,
    entityId,
    model,
    combinedMessages,
    imageFormat,
    aspectRatio,
  } = input;
  return async (dispatch, getState) => {
    try {
      const conversation = getAIConversationsQueryWithPublicUrlFromCache({
        id: aiConversationId,
      });

      const parentMessageId = _.get(conversation, "messages.edges[0].node.id");
      const inputPrompt = _.get(
        _.last(_.filter(combinedMessages, { role: "user" })),
        "content",
        ""
      );
      const response = await dispatch(
        getGeneratedImageUrl({
          entityType,
          entityId,
          model,
          inputPrompt,
          imageFormat,
          aspectRatio,
        })
      );

      // 6. create a message of the generated response from AI
      const { imageUrl } = response || {};
      const content = `<img src="${imageUrl}"  style="width:100%; height:auto;">`;
      if (!_.isEmpty(imageUrl)) {
        const response = await dispatch(
          createNewAIMessage({
            aiConversationId,
            authorType: "assistant",
            query: content,
            isUserInputMessage: false,
            metadata: {
              model,
            },
            awaitRefetchQueries: false,
            parentMessageId,
          })
        );

        const aiConversation = getAIConversationsQueryWithPublicUrlFromCache({
          id: aiConversationId,
        });
        const updatedData = update(aiConversation, {
          messages: {
            edges: {
              0: {
                node: {
                  $set: _.get(response, "data.ai.createAiMessage.response", {}),
                },
              },
            },
          },
        });
        writeAIConversationsQueryWithPublicUrlInCache({
          variables: {
            id: aiConversationId,
          },
          data: updatedData,
        });
      }
    } catch (e) {
      setCurrentAIState("");
    }
  };
};
